import React, { useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, UserX, UserCheck, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { NxAvatar } from "@/components/ui/NxAvatar";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import pt from "@/lib/i18n";
import { ROLE_BADGE_VARIANT } from "@/lib/rbac";
import { formatRelative } from "@/lib/utils";

const ROLE_OPTIONS = Object.entries(pt.users.roleLabels);

const UsersPage = () => {
    const [rows, setRows] = useState(null);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [formErr, setFormErr] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        password: "",
        role: "operations_manager",
        phone: "",
    });

    const load = async () => {
        setRows(null);
        try {
            const params = new URLSearchParams();
            if (roleFilter) params.set("role", roleFilter);
            if (statusFilter !== "") params.set("is_active", statusFilter);
            if (search) params.set("search", search);
            params.set("page_size", "50");
            const data = await apiGet(`/users?${params.toString()}`);
            setRows(data || []);
        } catch (e) {
            setRows([]);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleFilter, statusFilter]);

    useEffect(() => {
        const id = setTimeout(load, 350);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const toggleActive = async (u) => {
        try {
            await apiPatch(`/users/${u.id}`, { is_active: !u.is_active });
            load();
        } catch (e) {
            /* ignore */
        }
    };

    const handleDelete = async (u) => {
        if (!window.confirm(pt.users.confirmDeactivate)) return;
        try {
            await apiDelete(`/users/${u.id}`);
            load();
        } catch (e) {
            /* ignore */
        }
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        setFormErr("");
        setSubmitting(true);
        try {
            await apiPost("/users", form);
            setShowCreate(false);
            setForm({ full_name: "", email: "", password: "", role: "operations_manager", phone: "" });
            load();
        } catch (err) {
            const msg = err?.response?.data?.errors?.[0]?.message || pt.errors.generic;
            setFormErr(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="users-root">
            <PageHeader
                title={pt.users.title}
                subtitle={pt.users.subtitle}
                testId="users-header"
                right={
                    <NxButton
                        variant="copper"
                        onClick={() => setShowCreate(true)}
                        testId="add-user-button"
                        icon={<Plus size={16} />}
                    >
                        {pt.users.add}
                    </NxButton>
                }
            />

            {/* Filters */}
            <GlassCard padding="p-4" className="mb-5" testId="users-filters">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <GlassInput
                        prefix={<Search size={16} />}
                        placeholder={pt.users.searchPlaceholder}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        testId="users-search"
                    />
                    <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="glass-input"
                        data-testid="users-role-filter"
                    >
                        <option value="">{pt.users.filterRole}</option>
                        {ROLE_OPTIONS.map(([k, v]) => (
                            <option key={k} value={k}>
                                {v}
                            </option>
                        ))}
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="glass-input"
                        data-testid="users-status-filter"
                    >
                        <option value="">{pt.users.filterStatus}</option>
                        <option value="true">{pt.users.active}</option>
                        <option value="false">{pt.users.inactive}</option>
                    </select>
                </div>
            </GlassCard>

            {/* Table */}
            {rows === null ? (
                <GlassCard padding="p-6">
                    <div className="space-y-3">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} height="48px" />
                        ))}
                    </div>
                </GlassCard>
            ) : rows.length === 0 ? (
                <EmptyState
                    testId="users-empty"
                    title={pt.users.empty}
                    body="Ajuste os filtros ou crie um novo utilizador."
                    icon={<Plus size={24} />}
                />
            ) : (
                <GlassCard padding="p-0" testId="users-table">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[color:var(--glass-border)]">
                                    <th className="text-left px-6 py-3.5 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium">
                                        {pt.users.col_user}
                                    </th>
                                    <th className="text-left px-6 py-3.5 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium">
                                        {pt.users.col_email}
                                    </th>
                                    <th className="text-left px-6 py-3.5 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium">
                                        {pt.users.col_role}
                                    </th>
                                    <th className="text-left px-6 py-3.5 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium">
                                        {pt.users.col_lastLogin}
                                    </th>
                                    <th className="text-left px-6 py-3.5 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium">
                                        {pt.users.col_status}
                                    </th>
                                    <th className="px-6 py-3.5"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((u) => (
                                    <tr
                                        key={u.id}
                                        className="border-b border-[color:var(--glass-border-subtle)] hover:bg-[color:var(--copper-subtle)] transition-colors"
                                        data-testid={`user-row-${u.id}`}
                                    >
                                        <td className="px-6 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <NxAvatar name={u.full_name} src={u.avatar_url} size="md" />
                                                <span className="font-body text-sm text-[color:var(--slate-deep)] font-medium">
                                                    {u.full_name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3.5 font-mono text-xs text-[color:var(--slate-mid)]">
                                            {u.email}
                                        </td>
                                        <td className="px-6 py-3.5">
                                            <NxBadge variant={ROLE_BADGE_VARIANT[u.role] || "neutral"}>
                                                {pt.users.roleLabels[u.role] || u.role}
                                            </NxBadge>
                                        </td>
                                        <td className="px-6 py-3.5 font-mono text-xs text-[color:var(--slate-mid)]">
                                            {u.last_login_at ? formatRelative(u.last_login_at) : pt.users.neverLogged}
                                        </td>
                                        <td className="px-6 py-3.5">
                                            <NxBadge variant={u.is_active ? "success" : "neutral"}>
                                                {u.is_active ? pt.users.active : pt.users.inactive}
                                            </NxBadge>
                                        </td>
                                        <td className="px-6 py-3.5">
                                            <div className="flex items-center gap-1 justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleActive(u)}
                                                    className="p-2 rounded-md hover:bg-[color:var(--bg-elevated)] text-[color:var(--slate-mid)] hover:text-[color:var(--copper-dark)]"
                                                    title={u.is_active ? pt.users.inactive : pt.users.active}
                                                    data-testid={`toggle-active-${u.id}`}
                                                >
                                                    {u.is_active ? <UserX size={15} /> : <UserCheck size={15} />}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(u)}
                                                    className="p-2 rounded-md hover:bg-[rgba(192,57,43,0.08)] text-[color:var(--slate-mid)] hover:text-[color:var(--error)]"
                                                    data-testid={`delete-user-${u.id}`}
                                                    title={pt.common.delete}
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </GlassCard>
            )}

            {/* Create Modal */}
            <AnimatePresence>
                {showCreate && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/25 z-40"
                            onClick={() => setShowCreate(false)}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 16, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.97 }}
                            transition={{ duration: 0.28 }}
                            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(94vw,520px)] glass-elevated p-7"
                            data-testid="create-user-modal"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-display text-2xl text-[color:var(--slate-deep)]">
                                    {pt.users.add}
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setShowCreate(false)}
                                    className="p-1.5 rounded-md hover:bg-[color:var(--copper-subtle)]"
                                    data-testid="close-create-modal"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            {formErr && (
                                <p className="mb-3 text-sm text-[color:var(--error)] font-body">{formErr}</p>
                            )}
                            <form onSubmit={submitCreate} className="space-y-4">
                                <GlassInput
                                    label={pt.users.form.fullName}
                                    required
                                    value={form.full_name}
                                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                    testId="form-full-name"
                                />
                                <GlassInput
                                    label={pt.users.form.email}
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    testId="form-email"
                                />
                                <GlassInput
                                    label={pt.users.form.password}
                                    togglePassword
                                    required
                                    minLength={8}
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    testId="form-password"
                                />
                                <div>
                                    <label className="block text-xs font-medium text-[color:var(--slate-mid)] mb-1.5 tracking-wide uppercase">
                                        {pt.users.form.role}
                                    </label>
                                    <select
                                        value={form.role}
                                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                                        className="glass-input"
                                        data-testid="form-role"
                                    >
                                        {ROLE_OPTIONS.map(([k, v]) => (
                                            <option key={k} value={k}>
                                                {v}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <GlassInput
                                    label={pt.users.form.phone}
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    testId="form-phone"
                                />
                                <div className="flex justify-end gap-2 pt-2">
                                    <NxButton
                                        variant="secondary"
                                        type="button"
                                        onClick={() => setShowCreate(false)}
                                        testId="form-cancel"
                                    >
                                        {pt.users.form.cancel}
                                    </NxButton>
                                    <NxButton
                                        variant="copper"
                                        type="submit"
                                        loading={submitting}
                                        testId="form-submit"
                                    >
                                        {pt.users.form.submit}
                                    </NxButton>
                                </div>
                            </form>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default UsersPage;
